import {
    inject,
    Injectable
} from '@angular/core';

import {
    WaitDialogService
} from 'src/app/dialogs';

import {
    readJsonOrThrow
} from 'src/app/shared/net/fetch-response';

import {
    DigitalPaymentHandler,
    DigitalPaymentHandlerOptions,
    DigitalPaymentHandlerResult
} from 'src/app/shared/model/digital-payments.model';

import {
    Adyen
} from './adyen.model';

import {
    SalesPayments
} from 'tilby-models';

import {
    UserActiveSessionManagerService
} from 'src/app/core';

import {
    padCenter
} from 'src/app/shared/string-utils';

@Injectable({
    providedIn: 'root'
})
export class AdyenService implements DigitalPaymentHandler {
    private readonly userActiveSession = inject(UserActiveSessionManagerService);
    private readonly waitDialogService = inject(WaitDialogService);

    /**
     * Safely decodes a URL-encoded string.
     **/
    private safeDecode(str: string): string {
        try {
            return decodeURIComponent(str.replace(/\+/g, ' '));
        } catch {
            return str;
        }
    }

    /**
     * Aligns a label to the left side and its value to the right side of the line.
     * If the combined length of both strings exceeds the maximum allowed width,
     * the left label is truncated to ensure the value remains fully visible.
     *
     * @param left - The label text to align to the left.
     * @param right - The value text to align to the right.
     * @param width - The maximum column width of the line.
     * @returns The formatted line containing both left and right components.
     */
    private alignLeftRight(left: string, right: string, width: number): string {
        const combinedLength = left.length + right.length;

        if (combinedLength >= width) {
            // If the combined length exceeds the width, truncate the left text to fit the right value
            const maxLeftLength = width - right.length - 1;
            const truncatedLeft = left.substring(0, maxLeftLength);
            return truncatedLeft + ' ' + right;
        }

        const spaces = width - left.length - right.length;
        return left + ' '.repeat(spaces) + right;
    }

    /**
     * Generates a formatted text receipt from the provided JSON response.
     * Identifies the "CustomerReceipt" segment and processes each line 
     * to fit within the specified column limit.
     **/ 
    public getPaymentReceipt(paymentMethod: SalesPayments): string {
        const cols = 32;

        let paymentData: Adyen.TerminalAPIResponse;

        try {
            paymentData = JSON.parse(paymentMethod.payment_data!);
        } catch(err) {
            return '';
        }

        // Select the customer receipt copy
        const receipts = paymentData?.SaleToPOIResponse?.PaymentResponse?.PaymentReceipt;
        const customerReceipt = receipts?.find((receipt) => receipt.DocumentQualifier === 'CustomerReceipt');

        if (!customerReceipt) {
            return '';
        }

        const lines = customerReceipt.OutputContent.OutputText;
        const formattedReceipt: string[] = [];

        for (const line of lines) {
            const rawText = line.Text;

            // Parse key-value parameters from the text entry
            const params = new URLSearchParams(rawText);
            const key = params.get('key');
            const name = params.get('name') ? this.safeDecode(params.get('name')!) : '';
            const value = params.get('value') ? this.safeDecode(params.get('value')!) : '';

            // Handle blank lines or spacing (filler)
            if (key === 'filler') {
                formattedReceipt.push('');
                continue;
            }

            // Handle header placeholders
            if (key === 'header1' || key === 'header2') {
                continue;
            }

            // Align label to the left and value to the right if both are present
            if (name && value) {
                formattedReceipt.push(this.alignLeftRight(name, value, cols));
            }
            // Center align the text if only the label/name is present
            else if (name) {
                if (key === 'approved' || key === 'cardholderHeader') {
                    formattedReceipt.push(padCenter(name, cols));
                } else {
                    formattedReceipt.push(padCenter(name, cols));
                }
            }
        }

        return formattedReceipt.join('\n');
    }

    private sendTransactionRequest(apiUrl: string, merchantAccount: string, deviceId: string, apiKey: string, transactionRequest: Adyen.TerminalAPIRequest): Promise<Adyen.TerminalAPIResponse> {
        const url = merchantAccount
            ? `https://${apiUrl}/v1/merchants/${merchantAccount}/devices/${deviceId}/sync`
            : `https://${apiUrl}/sync`;

        return fetch(url, {
            method: 'POST',
            body: JSON.stringify(transactionRequest),
            headers: {
                'Content-Type': 'application/json',
                'x-API-key': apiKey
            }
        }).then(res => readJsonOrThrow(res));
    }

    public async payment(amount: number, options: DigitalPaymentHandlerOptions): Promise<DigitalPaymentHandlerResult> {
        const paymentMethod = options.paymentMethod;

        const {
            apiKey,
            apiUrl,
            deviceId,
            merchantAccount,
        } = (paymentMethod.configuration || {})

        if (!apiKey) {
            throw 'DIGITAL_PAYMENTS.ADYEN.MISSING_API_KEY';
        }

        if (!apiUrl) {
            throw 'DIGITAL_PAYMENTS.ADYEN.MISSING_API_URL';
        }

        if (!deviceId) {
            throw 'DIGITAL_PAYMENTS.ADYEN.MISSING_DEVICE_ID';
        }

        // Payment
        const transactionRequest: Adyen.TerminalAPIRequest = {
            SaleToPOIRequest: {
                MessageHeader: {
                    MessageCategory: 'Payment',
                    MessageClass: 'Service',
                    MessageType: 'Request',
                    ProtocolVersion: '3.0',
                    ServiceID: new Date().getTime().toString().slice(-10),
                    POIID: deviceId,
                    SaleID: this.userActiveSession.getSession()?.shop.name || 'Tilby'
                },
                PaymentRequest: {
                    PaymentTransaction: {
                        AmountsReq: {
                            Currency: options.sale.currency,
                            RequestedAmount: amount
                        }
                    },
                    SaleData: {
                        SaleTransactionID: {
                            TimeStamp: new Date().toISOString(),
                            TransactionID: options.sale.uuid!
                        }
                    }
                }
            }
        };

        const paymentPromise = this.sendTransactionRequest(apiUrl, merchantAccount, deviceId, apiKey, transactionRequest);

        const response = await this.waitDialogService.openDialog({
            data: {
                message: 'DIGITAL_PAYMENTS.INGENICO_17.COMPLETE_ON_POS',
                promise: paymentPromise,
            }
        }) as Adyen.TerminalAPIResponse;

        const paymentResponse = response.SaleToPOIResponse.PaymentResponse;

        if (paymentResponse.Response.Result !== 'Success') {
            throw paymentResponse.Response.ErrorCondition;
        }

        return {
            payment_data: JSON.stringify(response),
            unclaimed: false
        };
    }
}